import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import LearningDetailCard from '@/components/organisms/Card/LearningDetailCard';
import { academyLearningScheduleApi } from '@/services/api';

type UnitProgress = {
  id: number;
  code?: string;
  title: string;
  unitType?: string;
  unitCategory?: string;
  orderIndex?: number;
  status: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED';
  progress: number;
  score?: number;
  textbookTitle?: string;
  textbookId?: number;
};

export default function AcademyStudentScheduleDetail() {
  const { scheduleId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const category = (searchParams.get('category') || 'learning') as 'learning' | 'homework';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [title, setTitle] = useState('');
  const [startDate, setStartDate] = useState<string | undefined>(undefined);
  const [endDate, setEndDate] = useState<string | undefined>(undefined);
  const [progress, setProgress] = useState(0);
  const [units, setUnits] = useState<UnitProgress[]>([]);

  useEffect(() => {
    const fetchDetail = async () => {
      if (!scheduleId) return;
      setLoading(true);
      setError('');
      try {
        const res: any = await academyLearningScheduleApi.gradingDetail(Number(scheduleId));
        const data = res?.data || res;
        setTitle(String(data?.scheduleTitle ?? ''));
        setStartDate(data?.startDate ? String(data.startDate).slice(0, 10) : undefined);
        setEndDate(data?.endDate ? String(data.endDate).slice(0, 10) : undefined);
        setProgress(Number(data?.progress ?? 0));
        const textbooks = Array.isArray(data?.textbooks) ? data.textbooks : [];
        const flat: UnitProgress[] = [];
        textbooks.forEach((tb: any) => {
          const textbookTitle = tb?.textbookTitle ? String(tb.textbookTitle) : undefined;
          const u = Array.isArray(tb?.units) ? tb.units : [];
          u.forEach((it: any, idx: number) => {
            flat.push({
              id: Number(it.unitId ?? it.id ?? idx),
              code: String(it.unitCode ?? ''),
              title: String(it.unitTitle ?? ''),
              unitType: String(it.unitType ?? ''),
              unitCategory: String(it.unitCategory ?? ''),
              orderIndex: Number(it.orderIndex ?? idx),
              status: (String(it.status ?? 'NOT_STARTED').toUpperCase() as any),
              progress: Number(it.progress ?? 0),
              score: it.score != null ? Number(it.score) : undefined,
              textbookTitle: it.textbookTitle ? String(it.textbookTitle) : textbookTitle,
              textbookId: it.textbookId != null ? Number(it.textbookId) : (tb?.textbookId != null ? Number(tb.textbookId) : undefined),
            });
          });
        });
        setUnits(flat);
      } catch (e: any) {
        setError(e?.response?.data?.message || '상세 정보를 불러오지 못했습니다.');
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [scheduleId]);

  const status = useMemo<'completed' | 'in-progress' | 'waiting'>(() => {
    if (progress >= 100) return 'completed';
    const anyProgress = units.some(u => u.progress > 0 && u.progress < 100) || progress > 0;
    return anyProgress ? 'in-progress' : 'waiting';
  }, [progress, units]);

  const handleBack = () => {
    const from = searchParams.get('from');
    const category = (searchParams.get('category') || 'learning') as 'learning' | 'homework';
    if (from) {
      // 학원 학생 상세의 학습 탭으로 복귀
      navigate(`/academy/student/detail/${from}?tab=learning&category=${category}`);
    } else {
      navigate(-1);
    }
  };

  if (loading) {
    return (
      <main className='flex flex-1 justify-center items-center w-full bg-white lg:my-10 card md:py-5 md:bg-transparent'>
        <div className='text-center py-10 text-gray-500'>상세 정보를 불러오는 중...</div>
      </main>
    );
  }

  if (error) {
    return (
      <main className='flex flex-1 justify-center items-center w-full bg-white lg:my-10 card md:py-5 md:bg-transparent'>
        <div className='text-center py-10'>
          <p className='text-red-500 mb-4'>{error}</p>
          <button className='text-blue-500 hover:underline' onClick={handleBack}>뒤로가기</button>
        </div>
      </main>
    );
  }

  return (
    <main className='flex flex-1 justify-center w-full bg-white lg:my-10 card md:py-5 md:bg-transparent'>
      <div className='flex flex-col w-full'>
        <div className='px-5 pt-6 pb-3 lg:pt-0'>
          <button 
            onClick={handleBack}
            className='text-sm text-gray-500 hover:text-gray-700 mb-2'
          >
            ← 뒤로가기
          </button>
          <h1 className='heading1'>{title}</h1>
          {(startDate || endDate) && (
            <p className='text-sm text-gray-600 mt-1'>
              {startDate || ''}{endDate ? ` ~ ${endDate}` : ''}
            </p>
          )}
        </div>

        <div className='flex flex-col gap-4 px-5 pb-3'>
          <LearningDetailCard 
            status={status}
            progress={progress}
            type={category}
            units={units}
            scheduleId={Number(scheduleId)}
            disableNavigation={true}
          />
        </div>
      </div>
    </main>
  );
}


